import { createStore } from "./createStore";
import { createPlannerController } from "./dataController";
import { isoToDay, todayDay } from "./dayMath";
import { toggleExpanded } from "./rowModel";
import { summaryRangeOf } from "./summaryRange";
import {
  DAY_WIDTHS,
  centerDay,
  clampDay,
  createTimeline,
  isInsideTimeline,
  scrollLeftForCenter,
} from "./timelineGeometry";

export const CLOSED_TASK_MODAL = {
  isOpen: false,
  isEdit: false,
  taskId: null,
  resource: null,
  startDate: null,
  endDate: null,
  project: null,
  scope: null,
  group: null,
};

/** A guess of what is on screen before the planner is, so the first page already holds the days around today. */
const INITIAL_DAYS_BEFORE = 20;
const INITIAL_DAYS_AFTER = 40;

/**
 * Everything the planner screen works with, built once and handed around: the span of days it can show, the stores
 * that hold the data, the view settings, what is in the viewport and what the user has open, the controller that
 * keeps the data, and the actions that change them. Nothing here depends on React: the screen reads the stores
 * through selectors and calls the actions.
 *
 * @param api  the planner routes (see `plannerApi`)
 * @param notify  `{ error(title), saved() }` for what the user is told and what other parts of the app must hear
 * @param readShowHours  whether hours are shown, as remembered for the user
 * @param writeShowHours  remembers the choice
 * @param readPanelWidth  the width of the left panel, as remembered for the user
 * @param writePanelWidth  remembers the width
 */
export function createPlannerEngine({
  api,
  notify,
  isCanceled,
  retryAfterSeconds,
  readShowHours = () => true,
  writeShowHours = () => {},
  readPanelWidth = () => 310,
  writePanelWidth = () => {},
  now = () => new Date(),
}) {
  const today = todayDay(now());
  const timeline = createTimeline(today);
  const initialPanelWidth = readPanelWidth();

  const dataStore = createStore({});
  // The window of days that is drawn (a viewport beyond the screen on each side) and the rows that are drawn.
  const viewportStore = createStore({ firstDay: today - INITIAL_DAYS_BEFORE, lastDay: today + INITIAL_DAYS_AFTER, firstRow: 0, lastRow: 30 });
  const viewStore = createStore({
    viewType: "week",
    summaryPeriod: "this_week",
    /** The days of the period when the user picks their own: `{ startDay, endDay }`. */
    customPeriod: null,
    displayMode: "hours",
    showHours: readShowHours(),
    panelWidth: initialPanelWidth,
    expanded: {},
    /** A day to put in the middle once the timeline has been laid out for a new view. */
    centerOnNextLayout: null,
    /** Rows beneath this key are new and fade in. */
    enteringPrefix: null,
  });
  const uiStore = createStore({
    taskModal: CLOSED_TASK_MODAL,
    floatingCards: [],
    conflictsToasts: [],
    resourceInsights: null,
    projectFilter: null,
    resourceFilter: [],
  });

  /** The scroll container and the width of the left panel, which the screen registers once it exists. */
  const refs = { scroller: { current: null }, panelWidth: { current: initialPanelWidth } };

  let pendingDay = null;
  let enteringTimer = null;
  let floatingCounter = 0;

  const addConflictsToast = (toast) => uiStore.setState((state) => ({ ...state, conflictsToasts: [...state.conflictsToasts, toast] }));

  const controller = createPlannerController({
    store: dataStore,
    api,
    timeline,
    today,
    isCanceled,
    retryAfterSeconds,
    notify: {
      error: notify.error,
      saved: notify.saved,
      conflicts: (conflicts) => {
        const hasCapacityConflict = conflicts.some((conflict) => conflict.conflictType === "ResourceCapacity");
        addConflictsToast({
          type: hasCapacityConflict ? 0 : 1,
          title: hasCapacityConflict ? "Resource Capacity Conflict" : "Allocation Conflict",
          body: conflicts.map((conflict) => conflict.message).join("<br/> <br/> "),
        });
      },
    },
  });

  controller.setViewport({ firstDay: today - INITIAL_DAYS_BEFORE, lastDay: today + INITIAL_DAYS_AFTER, centerDay: today, resourceIds: [] });

  const view = () => viewStore.getState();
  const dayWidth = () => DAY_WIDTHS[view().viewType];
  const visibleWidth = (scroller) => Math.max(0, scroller.clientWidth - refs.panelWidth.current);

  // ── Moving around ───────────────────────────────────────────────────────

  /** Scrolls so a day is in the middle of the visible timeline. */
  function goToDay(day) {
    const target = clampDay(timeline, day);
    const scroller = refs.scroller.current;

    if (!scroller) {
      pendingDay = target;
      return;
    }
    scroller.scrollLeft = scrollLeftForCenter(timeline, target, dayWidth(), visibleWidth(scroller));
  }

  function goToDate(iso) {
    if (!iso) return;

    const day = isoToDay(iso);
    if (!isInsideTimeline(timeline, day)) notify.error("That date is outside the range the planner can show.");
    goToDay(day);
  }

  const goToToday = () => goToDay(today);

  function registerScroller(element) {
    refs.scroller.current = element;
    if (element && pendingDay !== null) {
      const day = pendingDay;
      pendingDay = null;
      goToDay(day);
    }
  }

  /** The day in the middle of what is visible now. */
  function currentCenterDay() {
    const scroller = refs.scroller.current;
    return scroller ? centerDay(timeline, scroller.scrollLeft, visibleWidth(scroller), dayWidth()) : today;
  }

  function setViewType(viewType) {
    if (viewType === view().viewType) return;
    viewStore.setState((state) => ({ ...state, viewType, centerOnNextLayout: currentCenterDay() }));
  }

  // ── Expanding ───────────────────────────────────────────────────────────

  function toggleExpand(resourceId, key) {
    viewStore.setState((state) => ({ ...state, expanded: toggleExpanded(state.expanded, resourceId, key), enteringPrefix: key }));

    // Rows that appear fade in, which only applies for a moment after the toggle.
    if (enteringTimer) clearTimeout(enteringTimer);
    enteringTimer = setTimeout(() => viewStore.setState((state) => ({ ...state, enteringPrefix: null })), 400);
  }

  // ── Filters ─────────────────────────────────────────────────────────────

  function applyFilters() {
    const { projectFilter, resourceFilter } = uiStore.getState();
    controller.setFilters({ projectId: projectFilter?.id || null, resourceIds: resourceFilter.map((resource) => resource.id) });
  }

  const setProjectFilter = (projectFilter) => {
    uiStore.setState((state) => ({ ...state, projectFilter }));
    // The whole life of a project means nothing without one.
    if (!projectFilter && view().summaryPeriod === "overall") viewStore.setState((state) => ({ ...state, summaryPeriod: "this_week" }));
    applyFilters();
  };

  const setResourceFilter = (resourceFilter) => {
    uiStore.setState((state) => ({ ...state, resourceFilter }));
    applyFilters();
  };

  // ── What the user has open ──────────────────────────────────────────────

  const openCreateTask = (resource, startDate, endDate, project = null, scope = null, group = null) =>
    uiStore.setState((state) => ({ ...state, taskModal: { isOpen: true, isEdit: false, taskId: null, resource, startDate, endDate, project, scope, group } }));

  const openEditTask = (taskId) =>
    uiStore.setState((state) => ({ ...state, taskModal: { ...CLOSED_TASK_MODAL, isOpen: true, isEdit: true, taskId } }));

  const closeTaskModal = () => uiStore.setState((state) => ({ ...state, taskModal: CLOSED_TASK_MODAL }));

  const openFloatingCard = (task, scope, project, allocation, position) => {
    floatingCounter += 1;
    uiStore.setState((state) => ({
      ...state,
      floatingCards: [...state.floatingCards, { id: `fc-${task.id}-${floatingCounter}`, task, scope, project, allocation, position }],
    }));
  };

  const closeFloatingCard = (id) => uiStore.setState((state) => ({ ...state, floatingCards: state.floatingCards.filter((card) => card.id !== id) }));

  const removeConflictsToast = (index) =>
    uiStore.setState((state) => ({ ...state, conflictsToasts: state.conflictsToasts.filter((_, i) => i !== index) }));

  const openResourceInsights = (resource) => uiStore.setState((state) => ({ ...state, resourceInsights: resource }));
  const closeResourceInsights = () => uiStore.setState((state) => ({ ...state, resourceInsights: null }));

  // ── Settings ────────────────────────────────────────────────────────────

  function setShowHours(showHours) {
    writeShowHours(showHours);
    viewStore.setState((state) => ({ ...state, showHours }));
  }

  function setPanelWidth(panelWidth) {
    refs.panelWidth.current = panelWidth;
    writePanelWidth(panelWidth);
    viewStore.setState((state) => ({ ...state, panelWidth }));
  }

  const setSummaryPeriod = (summaryPeriod) => viewStore.setState((state) => ({ ...state, summaryPeriod }));

  /** Summarises the days from `startDate` to `endDate` (yyyy-MM-dd) instead of one of the usual periods. */
  const setCustomPeriod = (startDate, endDate) => {
    const first = isoToDay(startDate);
    const last = isoToDay(endDate);
    viewStore.setState((state) => ({ ...state, summaryPeriod: "custom", customPeriod: { startDay: Math.min(first, last), endDay: Math.max(first, last) } }));
  };
  const setDisplayMode = (displayMode) => viewStore.setState((state) => ({ ...state, displayMode }));

  // ── Data ────────────────────────────────────────────────────────────────

  // The controller keeps what the summary period needs, wherever the viewport is. The days depend on the period
  // chosen, the project (for its whole life) and, once its dates arrive, the data.
  let summaryRange = null;
  const syncSummaryRange = () => {
    const { projectFilter } = uiStore.getState();
    const project = projectFilter ? dataStore.getState().data?.projects?.[projectFilter.id] : null;
    const next = summaryRangeOf({ summaryPeriod: view().summaryPeriod, customPeriod: view().customPeriod, project, today });

    if (summaryRange && summaryRange.startDay === next.startDay && summaryRange.endDay === next.endDay) return;
    summaryRange = next;
    controller.setSummaryRange(next);
  };
  const stopSyncingSummaryRange = [viewStore, uiStore, dataStore].map((store) => store.subscribe(syncSummaryRange));
  syncSummaryRange();

  /** Something was saved elsewhere (a task): what is held may be out of date. */
  function dataChanged() {
    notify.saved();
    controller.invalidateAll();
  }

  return {
    timeline,
    today,
    dataStore,
    viewStore,
    viewportStore,
    uiStore,
    controller,
    refs,
    actions: {
      goToDay,
      goToDate,
      goToToday,
      registerScroller,
      setViewType,
      toggleExpand,
      setProjectFilter,
      setResourceFilter,
      applyFilters,
      openCreateTask,
      openEditTask,
      closeTaskModal,
      openFloatingCard,
      closeFloatingCard,
      addConflictsToast,
      removeConflictsToast,
      openResourceInsights,
      closeResourceInsights,
      setShowHours,
      setPanelWidth,
      setSummaryPeriod,
      setCustomPeriod,
      setDisplayMode,
      dataChanged,
      refresh: () => controller.invalidateAll(),
      reload: () => controller.reload(),
      loadNextPage: () => controller.loadNextPage(),
      updateAllocation: (resourceId, taskId, startDate, endDate) => controller.updateAllocation(resourceId, taskId, startDate, endDate),
    },
    dispose() {
      stopSyncingSummaryRange.forEach((stop) => stop());
      if (enteringTimer) clearTimeout(enteringTimer);
      controller.dispose();
    },
  };
}
